import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import PageMeta from "@/components/PageMeta";
import { api } from "@/lib/api";

function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }); } catch { return ""; }
}

export default function DepartmentDetail() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/departments/${slug}`).then((r) => setD(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <SiteLayout>
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-32">
          <h1 className="h-display text-4xl">Department not found.</h1>
          <Link to="/expertise" className="link-grow mt-6 inline-flex">Back to Expertise</Link>
        </div>
      </SiteLayout>
    );
  }

  if (!d) return <SiteLayout><div /></SiteLayout>;

  return (
    <SiteLayout>
      <PageMeta
        title={d.seo_title || `${d.title} | Carlton Elleker Chase`}
        description={d.seo_description || d.tagline || d.summary}
      />
      <article className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 pt-10 md:pt-20" data-testid="expertise-detail">
        <Link to="/expertise" className="inline-flex items-center gap-2 overline text-graphite/60 hover:text-[#1E3668]">
          <ArrowLeft size={14} /> All departments
        </Link>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-16">
          <div className="md:col-span-12">
            <h1 className="h-display text-[clamp(2.4rem,7vw,6.4rem)] max-w-[14ch]">{d.title}</h1>
            {d.tagline && (
              <p className="mt-8 text-xl md:text-2xl text-[#1E3668] max-w-3xl leading-snug">{d.tagline}</p>
            )}
            {d.summary && (
              <div className="mt-10 max-w-3xl space-y-5 text-graphite/85 leading-relaxed text-base md:text-lg">
                {d.summary.split(/\n\s*\n/).map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}

            {d.head_lawyer && (
              <div className="mt-12 inline-flex items-center gap-4 hairline px-5 py-4" data-testid="head-of-department">
                {d.head_lawyer.photo_url && (
                  <img src={d.head_lawyer.photo_url} alt={d.head_lawyer.name} className="w-14 h-14 object-cover bw-portrait" />
                )}
                <div>
                  <p className="text-xs text-graphite/55">Head of Department</p>
                  <Link to={`/people/${d.head_lawyer.slug}`} className="font-semibold hover:text-[#1E3668]">{d.head_lawyer.name}</Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </article>

      {/* Services list */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4 md:sticky md:top-32 self-start">
            <p className="overline text-graphite/55">What we do</p>
            <h2 className="mt-5 h-display text-3xl md:text-5xl">{d.services.length} services</h2>
            <p className="mt-6 text-graphite/75 leading-relaxed max-w-sm">Each service is led by senior lawyers and supported across the department.</p>
          </div>
          <ul className="md:col-span-8 hairline-top" data-testid="services-list">
            {d.services.map((s, i) => (
              <li key={s.slug} className="num-list-row hairline-bottom">
                <Link to={`/services/${s.slug}`} className="grid grid-cols-12 gap-3 py-6 md:py-7 items-baseline" data-testid={`service-link-${s.slug}`}>
                  <span className="col-span-2 md:col-span-1 overline text-graphite/50">{String(i + 1).padStart(2, "0")}</span>
                  <span className="col-span-9 md:col-span-10 num-title text-lg md:text-xl font-medium tracking-tight">{s.title}</span>
                  <span className="col-span-1 justify-self-end num-arrow"><ArrowUpRight size={18} className="text-[#1E3668]" /></span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Related lawyers */}
      {d.lawyers?.length > 0 && (
        <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-20 hairline-top">
          <h2 className="h-display text-3xl md:text-5xl">Lawyers in this department</h2>
          <div className="mt-12 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4 md:gap-6" data-testid="dept-lawyers-grid">
            {d.lawyers.map((l) => (
              <Link to={`/people/${l.slug}`} key={l.slug} className="group block" data-testid={`dept-lawyer-${l.slug}`}>
                <div className="aspect-square overflow-hidden bg-[#EEEAE3]">
                  {l.photo_url && <img src={l.photo_url} alt={l.name} className="w-full h-full object-cover bw-portrait" />}
                </div>
                <div className="mt-2">
                  <p className="text-sm font-medium leading-tight group-hover:text-[#1E3668] transition-colors">{l.name}</p>
                  <p className="text-xs text-graphite/55 mt-0.5">{l.title}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Related articles */}
      {d.articles?.length > 0 && (
        <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-20 hairline-top" data-testid="dept-articles">
          <h2 className="h-display text-3xl md:text-5xl">Recent writing from {d.title}</h2>
          <div className="mt-12 hairline-top">
            {d.articles.map((a) => (
              <Link key={a.slug} to={`/insights/${a.slug}`} className="num-list-row hairline-bottom grid grid-cols-12 gap-4 py-6 md:py-7">
                <p className="col-span-12 md:col-span-3 overline text-graphite/55">{formatDate(a.published_at)}</p>
                <p className="col-span-12 md:col-span-8 num-title text-lg md:text-xl font-medium">{a.title}</p>
                <span className="hidden md:flex md:col-span-1 justify-end num-arrow"><ArrowUpRight size={18} className="text-[#1E3668]" /></span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </SiteLayout>
  );
}
